import { HStack, VStack, Text, Icon } from '@genial/components';
import { BoxBorder, Color, Spacing } from '@genial/design-tokens';
import { Box, Input as NativeInput, Pressable } from 'native-base';
import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator } from 'react-native';
import { useRecoilCallback } from 'recoil';

import { customerAutocompleteQuery } from '../../../../../store/customer/selector';
import { CustomerFinancialResponseNormalized } from '../../../../../store/customer/types';

import {
  RequestModalFieldShell,
  requestModalFieldPadding,
  requestModalInputFontSize,
} from './requestModalFieldStyles';

const CUSTOMER_ID_PARAM = 'customerId';
const CUSTOMER_ACCOUNT_PARAM = 'customerAccountNumber';
const MIN_SEARCH_LENGTH = 3;

type ProfitabilityCustomerFieldProps = {
  additionalParameters: Record<string, string>;
  updateAdditionalParameter: (name: string, value: string) => void;
};

export const PROFITABILITY_CUSTOMER_PARAM_NAMES = [
  CUSTOMER_ID_PARAM,
  CUSTOMER_ACCOUNT_PARAM,
] as const;

const formatSelectedCustomerLabel = (customer: CustomerFinancialResponseNormalized) =>
  `${customer.customerNameNormalized} · ${customer.sinacorAccountNormalized}`;

export const ProfitabilityCustomerField: React.FC<ProfitabilityCustomerFieldProps> = ({
  additionalParameters,
  updateAdditionalParameter,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [customersResult, setCustomersResult] = useState<CustomerFinancialResponseNormalized[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<
    CustomerFinancialResponseNormalized | undefined
  >();
  const [showNotFound, setShowNotFound] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const fetchCustomersByTerm = useRecoilCallback(
    ({ snapshot }) =>
      async (term: string) =>
        snapshot.getPromise(customerAutocompleteQuery({ searchTerm: term })),
    []
  );

  const applyCustomerSelection = useCallback(
    (customer: CustomerFinancialResponseNormalized) => {
      setSelectedCustomer(customer);
      setInputValue(formatSelectedCustomerLabel(customer));
      setCustomersResult([]);
      setShowNotFound(false);
      updateAdditionalParameter(CUSTOMER_ID_PARAM, String(customer.customerId));
      updateAdditionalParameter(CUSTOMER_ACCOUNT_PARAM, String(customer.accountNumber));
    },
    [updateAdditionalParameter]
  );

  const clearCustomerParams = useCallback(() => {
    updateAdditionalParameter(CUSTOMER_ID_PARAM, '');
    updateAdditionalParameter(CUSTOMER_ACCOUNT_PARAM, '');
  }, [updateAdditionalParameter]);

  const handleChangeText = useCallback(
    (value: string) => {
      setInputValue(value);
      setShowNotFound(false);
      setCustomersResult([]);
      if (selectedCustomer) {
        setSelectedCustomer(undefined);
        clearCustomerParams();
      }
    },
    [clearCustomerParams, selectedCustomer]
  );

  const handleSearch = useCallback(async () => {
    if (selectedCustomer || isSearching) {
      return;
    }

    const term = inputValue.trim();
    if (term.length < MIN_SEARCH_LENGTH) {
      setCustomersResult([]);
      setShowNotFound(false);
      return;
    }

    setIsSearching(true);
    setShowNotFound(false);
    setCustomersResult([]);

    try {
      const response = await fetchCustomersByTerm(term);
      const results = Array.isArray(response?.data) ? response.data : [];

      if (results.length === 0) {
        setShowNotFound(true);
        return;
      }

      if (results.length === 1) {
        applyCustomerSelection(results[0]);
        return;
      }

      setCustomersResult(results);
    } catch {
      setShowNotFound(true);
    } finally {
      setIsSearching(false);
    }
  }, [applyCustomerSelection, fetchCustomersByTerm, inputValue, isSearching, selectedCustomer]);

  const handleClear = useCallback(() => {
    setInputValue('');
    setCustomersResult([]);
    setShowNotFound(false);
    setSelectedCustomer(undefined);
    clearCustomerParams();
  }, [clearCustomerParams]);

  useEffect(() => {
    if (!additionalParameters[CUSTOMER_ID_PARAM] && selectedCustomer) {
      setSelectedCustomer(undefined);
      setInputValue('');
    }
  }, [additionalParameters, selectedCustomer]);

  const showClear = Boolean(inputValue) || Boolean(selectedCustomer);
  const canSearch =
    !selectedCustomer && inputValue.trim().length >= MIN_SEARCH_LENGTH && !isSearching;

  return (
    <VStack space="stack.quarck">
      <Text variant="regular">Cliente</Text>
      <RequestModalFieldShell>
        <HStack alignItems="center" space="inline.quarck" p={requestModalFieldPadding}>
          <NativeInput
            testID="report-generator-profitability-customer-input"
            flex={1}
            value={inputValue}
            onChangeText={handleChangeText}
            onSubmitEditing={() => {
              void handleSearch();
            }}
            returnKeyType="search"
            placeholder="Pesquise por nome, conta ou CPF"
            variant="unstyled"
            fontSize={requestModalInputFontSize}
            p={0}
          />
          {isSearching ? (
            <ActivityIndicator size="small" color={Color['brand.tertiary.light']} />
          ) : null}
          {showClear && !isSearching ? (
            <Pressable
              testID="report-generator-profitability-customer-clear"
              onPress={handleClear}
              accessibilityLabel="Limpar cliente">
              <Icon name="Close" size="xxxs" color={Color['neutral.medium']} />
            </Pressable>
          ) : null}
          {!isSearching && !selectedCustomer ? (
            <Pressable
              testID="report-generator-profitability-customer-search"
              onPress={() => {
                void handleSearch();
              }}
              disabled={!canSearch}
              opacity={canSearch ? 1 : 0.4}
              accessibilityLabel="Buscar cliente">
              <Icon name="Search" size="xxxs" color={Color['brand.primary.principal']} />
            </Pressable>
          ) : null}
        </HStack>
      </RequestModalFieldShell>

      {customersResult.length > 0 ? (
        <VStack bg="backgroundshade" borderRadius={BoxBorder.thick} overflow="hidden">
          {customersResult.map((customer) => (
            <Pressable
              key={`${customer.customerId}-${customer.accountNumber}`}
              testID={`report-generator-profitability-customer-option-${customer.customerId}`}
              onPress={() => applyCustomerSelection(customer)}
              px={requestModalFieldPadding}
              py={requestModalFieldPadding}>
              <Text numberOfLines={1} variant="regular">
                {customer.customerNameNormalized}
              </Text>
              <HStack justifyContent="space-between" space={Spacing['stack.xxxs']}>
                <Text color="neutral.medium" variant="regular">
                  {customer.cpfCnpjNormalized}
                </Text>
                <Text color="neutral.medium" variant="regular">
                  {customer.sinacorAccountNormalized}
                </Text>
              </HStack>
            </Pressable>
          ))}
        </VStack>
      ) : null}

      {showNotFound && !isSearching ? (
        <Box
          bg="backgroundshade"
          borderRadius={BoxBorder.thick}
          px={requestModalFieldPadding}
          py={requestModalFieldPadding}>
          <Text variant="regular">Nenhum resultado corresponde a sua pesquisa.</Text>
          <Text variant="regular" color="neutral.medium">
            Verifique seu nível de acesso e tente novamente.
          </Text>
        </Box>
      ) : null}
    </VStack>
  );
};
